import { nl } from 'date-fns/locale';
import { List, Map, Set } from "immutable";
import * as React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { Action, LoadedAsyncState, mk_widget, Widget } from "widgets-for-react";
import { dateOnlyFormat, uniqueMonthFormat } from "../../../../../custom_utils/moment";
import { ExpandItem, ExpandProps } from "../../../../components/library/expandItem";
import { Loader } from "../../../../components/loader";
import { OvaticLocation } from "../../LocationState";
import { Means } from "../LocationPageState";
import { getAvailableLocationTimes, postLocationOption } from "./LocationOptionFormApi";
import { allFields, Field, initialState, LocationOptionData, LocationOptionFormProps, LocationOptionFormState, MeansId, Message, minimumRentalDate, setFieldOnState, setMeans, setVisited, validateFormData } from "./LocationOptionFormState";
import { fastFields } from './LocationOptionFormValidation';
import { LocationOptionPartsOfDayInput } from "./TimeslotInput/TimeslotInputLayout";
import { initialTimeslotSelection, TimeslotAvailability } from "./TimeslotInput/TimeslotInputState";
import moment = require("moment");
import debounce = require('lodash.debounce');

registerLocale('nl', nl)

const FORM_ELEMENT_ID = 'zv-form'

function Expand(
  props: ExpandProps,
): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => ExpandItem({ onClick: () => onDone(s => !s), ...props })
  })
}

const MeansCategory = (title: string,
  means: List<Means>,
  meansSelection: LocationOptionData['means'],
  updateMeans: (id: MeansId, amount: number) => void,
  expandedCategories: Set<string>,
  toggleCategory: (category: string) => void
) => Expand({
  key: `expand--${title}`,
  open: expandedCategories.has(title),
  title: title,
  child: <>{means.map(m => MeansInput(m, meansSelection.get(m.meansID, 0), updateMeans)).toArray()}</>
}).run(_ => toggleCategory(title))

const MeansInput = (means: Means, currentAmount: number, updateMeans: (id: MeansId, amount: number) => void) =>
  <div key={`option-form-item-${means.meansID}`} className="zv-form__group">
    <div className="zv-form__checkbox form__checkbox">
      <label className="" htmlFor={`option-form-item-${means.meansID}-checkbox`}>{means.name}</label>
    </div>
    <div className="zv-form__amount">
      <label className="zv-form__sublabel" htmlFor={`option-form-item-${means.meansID}-number`}>Aantal</label>
      <input className="zv-form__input form__input"
        id={`option-form-item-${means.meansID}-number`}
        name="Aantal"
        type="number"
        min={0}
        max={means.count}
        value={currentAmount}
        onChange={e => updateMeans(means.meansID, e.target.valueAsNumber)} />
    </div>
  </div>

export class LocationOptionForm extends React.Component<LocationOptionFormProps, LocationOptionFormState> {
  constructor(props: LocationOptionFormProps) {
    super(props)
    this.state = { ...initialState }
  }

  componentDidUpdate(prevProps: Readonly<LocationOptionFormProps>): void {
    // Load availabities for current and next month
    this.loadMonthAvailabilities(moment().format(uniqueMonthFormat))
    this.loadMonthAvailabilities(moment().add(1, 'month').format(uniqueMonthFormat))

    // Ensure there is a proper selection for Seatplan once the location loaded
    if (prevProps.location.kind != 'loaded' && this.props.location.kind == 'loaded') {
      const defaultSeatplanId = this.firstSeatplanId(this.props.location)
      this.setField('seatplanId')(defaultSeatplanId)
    }
  }

  setField = setFieldOnState(this.setState.bind(this))

  set = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setField(field)(e.target.value, () => {
      const fields = this.state.visited.add(field)
      fastFields.has(field) ? this.validate(fields) : this.validateVisitedAfterDelay(fields)
    })
  }

  setVisit = setVisited(this.setState.bind(this))
  visit = (field: Field) => () => setVisited(this.setState.bind(this))(field)

  async loadMonthAvailabilities(monthLabel: string) {
    if (this.state.monthlyAvailabilities.has(monthLabel)) return

    this.setState((s0) => {
      const monthlyAvailabilities = s0.monthlyAvailabilities.set(monthLabel, Map())
      return { ...s0, monthlyAvailabilities }
    },
      async () => {
        const month = await getAvailableLocationTimes(moment(monthLabel).toDate(), this.props.locationId)
        this.setState((s1) => {
          const monthlyAvailabilities = s1.monthlyAvailabilities.set(monthLabel, month)
          return { ...s1, monthlyAvailabilities }
        })
      }
    )
  }

  isDatePickable(date: Date): boolean {
    const monthLabel = moment(date).format(uniqueMonthFormat)
    const month = this.state.monthlyAvailabilities.get(monthLabel)

    if (month == undefined) return false

    const key = moment(date).format(dateOnlyFormat)
    const day = month.get(key)

    if (day == undefined) return false

    return day.morning || day.afternoon || day.evening
  }

  firstSeatplanId = (location: LoadedAsyncState<OvaticLocation>) =>
    List(location.value.seatplans)
      .sortBy(s => s.name.toLocaleLowerCase().replace(/[^a-z0-9]/g, ''))
      .first()
      .seatplanID

  toggleCategory(category: string) {
    const oldCategories = this.state.expandedMeansCategories
    const categories = oldCategories.has(category)
      ? oldCategories.delete(category)
      : oldCategories.add(category)

    this.setState(prev => ({ ...prev, expandedMeansCategories: categories }))
  }

  updateMeans(id, amount) {
    this.setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        means: setMeans(prevState.data.means)(id, amount)
      }
    }))
  }

  submit() {
    const { location, means } = this.props
    if (location.kind != 'loaded' || means.kind != 'loaded') return

    this.setState(
      prevState => ({ ...prevState, submitting: true }),
      async () => {
        const message: Message = await postLocationOption(location.value, this.state.data, means.value)
          .then((result) => {
            if (result.kind != 'result') throw result.kind
            const m: Message = { kind: "success", message: "Formulier is succesvol verstuurd naar Bibliotheek Kennemerwaard. Wij nemen zo spoedig mogelijk contact met u op." }
            return m
          })
          .catch((e) => {
            console.error('Submitting form failed', e);
            const m: Message = { kind: "error", message: "Er is iets mis gegaan tijdens het versturen van het formulier" }
            return m
          })

        // Ensure the form gets reset if submit was successful
        const success = message.kind == 'success'
        const nextState = success ? initialState : this.state
        const { visited, expandedMeansCategories, data } = nextState
        if (success) data.seatplanId = this.firstSeatplanId(location)

        this.setState(
          prevState => ({ ...prevState, message, data, visited, expandedMeansCategories, submitting: false }),
          () => window.scroll({
            top: window.document.getElementById(FORM_ELEMENT_ID).offsetTop,
            behavior: 'smooth'
          })
        )
      }
    )
  }

  availabilityForSelectedDate(): TimeslotAvailability {
    const date = moment(this.state.data.date)
    const monthLabel = date.format(uniqueMonthFormat)
    const dateLabel = date.format(dateOnlyFormat)

    return this.state.monthlyAvailabilities
      .get(monthLabel, Map())
      .get(dateLabel, initialTimeslotSelection)
  }

  maxParticipants() {
    const { location } = this.props
    const { seatplanId } = this.state.data
    return location.kind != 'loaded' ? Number.MAX_VALUE :
      seatplanId
        ? location.value.seatplans.find(s => s.seatplanID == seatplanId).capacity // Max participants of selected plan
        : List(location.value.seatplans).max(s => s.capacity).capacity            // or max participants of all plans
  }

  validate(fields: Set<Field>) {
    const maxParticipants = this.maxParticipants()
    validateFormData(this.setState.bind(this), { maxParticipants })(fields)
  }

  validateVisitedAfterDelay = debounce((fields: Set<Field>) => this.validate(fields), 1000)

  render() {
    const { props: { means, location }, state, set, setField, visit } = this

    const { data, visited, validationMessages, submitting, message } = state
    const selectedMeans = data.means

    const formIsNotValid = validationMessages.size > 0
    const maxParticipants = this.maxParticipants()

    const meansCategories = means.kind != 'loaded' ? <Loader key="loader--means" /> :
      List(means.value)
        .groupBy((m) => m.category.description)
        .filter((_, description) => !description.includes("online niet zichtbaar"))
        .map((groupedMeans, description) => MeansCategory(
          description,
          groupedMeans.toList(),
          selectedMeans,
          (id, amount) => !submitting && this.updateMeans(id, amount),
          this.state.expandedMeansCategories,
          (category) => !submitting && this.toggleCategory(category)
        ))
        .toArray()

    const seatplanOptions = location.kind != 'loaded' ? <></> :
      List(location.value.seatplans)
        .sortBy(s => s.name.toLocaleLowerCase().replace(/[^a-z0-9]/g, ''))
        .map(s => <option key={s.seatplanID} value={s.seatplanID}>{`${s.name} (max. ${s.capacity})`}</option>)
        .toArray()

    const validationClass = (field: Field) => {
      if (validationMessages.has(field)) return 'input--error'
      if (visited.has(field)) return 'input--valid'
      return 'input--unvalidated'
    }

    return (
      <div id={FORM_ELEMENT_ID} className={`zv-form container--mg align--center${submitting ? ' zv-form--disabled' : ''}`}>
        <div className="zv-form__inner">
          <h2 className="zv-form__title title--l container--xs">Interesse? Vul het formulier in</h2>

          {message &&
            <div className={`announcement__inner ${message.kind} text--styled`} role="alert">
              {message.message}
            </div>
          }

          <form onSubmit={(e) => e.preventDefault()} className="zv-form__form">
            <div className="zv-form__field zv-form__field--2-fifth">
              <label htmlFor="firstName" className="zv-form__label">Voornaam</label>
              <input
                disabled={submitting}
                type="text"
                id="firstName"
                className={`zv-form__input form__input ${validationClass('firstName')}`}
                value={data.firstName}
                onFocus={visit('firstName')}
                onChange={set('firstName')}
              />
            </div>
            <div className="zv-form__field zv-form__field--1-fifth">
              <label htmlFor="infix" className="zv-form__label">Tussenvoegsel</label>
              <input
                disabled={submitting}
                type="text"
                id="infix"
                className={`zv-form__input form__input ${validationClass('infix')}`}
                value={data.infix}
                onFocus={visit('infix')}
                onChange={set('infix')}
              />
            </div>
            <div className="zv-form__field zv-form__field--2-fifth">
              <label htmlFor="lastName" className="zv-form__label">Achternaam</label>
              <input
                disabled={submitting}
                type="text"
                id="lastName"
                className={`zv-form__input form__input ${validationClass('lastName')}`}
                value={data.lastName}
                onFocus={visit('lastName')}
                onChange={set('lastName')}
              // className={s.errors.v.kind === "r" && s.errors.v.v.errors.lastName.length > 0 ? "input-error" : ""}
              />
            </div>


            <div className="zv-form__field zv-form__field--half">
              <label htmlFor="email" className="zv-form__label">E-mail</label>
              <input
                disabled={submitting}
                type="email"
                id="email"
                className={`zv-form__input form__input ${validationClass('email')}`}
                value={data.email}
                onFocus={visit('email')}
                onChange={set('email')}
              />
            </div>
            <div className="zv-form__field zv-form__field--half">
              <label htmlFor="phoneNumber" className="zv-form__label">Telefoonnummer</label>
              <input
                disabled={submitting}
                type="text"
                id="phoneNumber"
                className={`zv-form__input form__input ${validationClass('phoneNumber')}`}
                value={data.phoneNumber}
                onFocus={visit('phoneNumber')}
                onChange={set('phoneNumber')}
              // className={s.errors.v.kind === "r" && s.errors.v.v.errors.lastName.length > 0 ? "input-error" : ""}
              />
            </div>

            <div className="zv-form__field">
              <label htmlFor="companyName" className="zv-form__label">Bedrijfsnaam</label>
              <input
                disabled={submitting}
                type="text"
                id="companyName"
                className={`zv-form__input form__input ${validationClass('companyName')}`}
                value={data.companyName}
                onFocus={visit('companyName')}
                onChange={set('companyName')}
              />
            </div>


            <div className="zv-form__field zv-form__field--half">
              <label htmlFor="seatplanId" className="zv-form__label">Opstelling</label>
              <div className="form__select">
                <select
                  disabled={submitting}
                  onFocus={visit('seatplanId')}
                  onChange={e => setField('seatplanId')(Number(e.target.value))}
                  value={data.seatplanId}
                  id="zvf-type"
                >
                  {seatplanOptions}
                </select>
              </div>
            </div>

            <div className="zv-form__field zv-form__field--half">
              <label htmlFor="participants" className="zv-form__label">Verwachte aantal deelnemers</label>
              <input
                disabled={submitting}
                type="number"
                min={0}
                max={maxParticipants}
                id="participants"
                className={`zv-form__input form__input ${validationClass('participants')}`}
                value={data.participants}
                onFocus={visit('participants')}
                onChange={set('participants')}
              />
            </div>


            <div className="zv-form__field zv-form__field--half">
              <label htmlFor="orderDate" className="zv-form__label">Gewenste datum</label>
              <div className="form__item--icon right">
                <DatePicker
                  locale="nl"
                  dateFormat="EEEE, dd MMMM yyyy"
                  minDate={minimumRentalDate}
                  selected={data.date}
                  onFocus={visit('date')}
                  onChange={newDate => {
                    setField('date')(newDate, () => {
                      // Selecting a date, then timeslots, then a new date may lead to
                      // a previously selected time slot to become unavailable on the new date.
                      // The following will reset the timeslot selection when this happens.
                      const available = this.availabilityForSelectedDate()
                      const selected = this.state.data.timeslots

                      if (
                        selected.morning && !available.morning ||
                        selected.afternoon && !available.afternoon ||
                        selected.evening && !available.evening
                      ) setField('timeslots')(initialTimeslotSelection, () => this.validate(this.state.visited))
                    })
                  }}
                  filterDate={(date: Date) => this.isDatePickable(date)}
                  onMonthChange={(date) => {
                    this.loadMonthAvailabilities(moment(date).format(uniqueMonthFormat))
                    this.loadMonthAvailabilities(moment(date).add(1, 'month').format(uniqueMonthFormat))
                  }}
                />
                <svg className="form__icon" width="20px" height="22px" aria-hidden="true"><use xlinkHref="#icon--calendar-dnb" /></svg>
              </div>
            </div>

            <div className="zv-form__field zv-form__field--half">
              <LocationOptionPartsOfDayInput
                date={data.date}
                selection={data.timeslots}
                availability={this.availabilityForSelectedDate()}
                updateSelection={newSelection => setField('timeslots')(newSelection, () => {
                  this.visit('timeslots')
                  this.validate(visited.add('timeslots'))
                })}
              />
            </div>


            <div className="zv-form__field zv-form__field--extra" key="form__means">
              <h3 className="zv-form__subtitle title--xs" key="means__title">Aanvullende zaken</h3>
              <ul className="block__list expand__list">
                {meansCategories}
              </ul>
            </div>

            <div className="zv-form__field">
              <label htmlFor="notes" className="zv-form__label">Opmerkingen, bijv. catering</label>
              <textarea
                disabled={submitting}
                name="notes"
                id="notes"
                className="zv-form__input form__textarea"
                value={data.note}
                onChange={e => setField('note')(e.target.value)}
              />
            </div>


            {formIsNotValid && (
              <div className="announcement__inner error text--styled" role="alert">
                <ul>
                  {validationMessages.map((value, key) => <li key={key}>{value}</li>).toArray()}
                </ul>
              </div>
            )}

            <div className="zv-form__submit">
              <button
                disabled={formIsNotValid || submitting}
                type="submit"
                onMouseOver={() => this.validate(allFields)}
                onClick={() => this.submit()}
                className="button button--primary button--arrow">
                <span className="button__label">{submitting
                  ? "Aanvraag wordt verzonden"
                  : "Aanvraag verzenden"
                }</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
}

{/* TH: Added to styleguide! */ }
